import { useEffect, useState, useMemo, useRef } from 'react';
import { useCinemaMode } from '@/contexts/CinemaModeContext';
import { useThemePalette } from '@/hooks/useThemePalette';
import { themePresets } from '@/data/themePresets';
import lunarMoonCinema from '@/assets/lunar-moon-cinema-v3.png';

interface Star {
  id: number;
  x: number;
  y: number;
  size: number;
  opacity: number;
  layer: 'far' | 'mid' | 'near';
  twinkleDelay: number;
  twinkleDuration: number;
  colorIndex: number;
}

// Get theme colors for stars and planet
const getThemeColors = (themeId: string): string[] => {
  const theme = themePresets.find(t => t.id === themeId);
  if (!theme) return ['#ffffff', '#aaaaaa', '#888888', '#666666', '#444444'];
  return theme.previewColors;
};

// Generate stars across layers with color indices
const generateStars = (count: number): Star[] => {
  const stars: Star[] = [];
  const layers: ('far' | 'mid' | 'near')[] = ['far', 'mid', 'near'];

  for (let i = 0; i < count; i++) {
    const layer = layers[Math.floor(Math.random() * 3)];

    stars.push({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 78, // slightly lower so it feels fuller like Lunar Moon
      size:
        layer === 'near'
          ? Math.random() * 3.4 + 2.2
          : layer === 'mid'
            ? Math.random() * 2.4 + 1.1
            : Math.random() * 1.7 + 0.7,
      opacity:
        layer === 'near'
          ? Math.random() * 0.35 + 0.65
          : layer === 'mid'
            ? Math.random() * 0.3 + 0.5
            : Math.random() * 0.28 + 0.35,
      layer,
      twinkleDelay: Math.random() * 10,
      twinkleDuration: Math.random() * 4 + 4,
      colorIndex: Math.floor(Math.random() * 5),
    });
  }

  return stars;
};

const CinemaModeBackground = () => {
  const { cinemaMode, prefersReducedMotion } = useCinemaMode();
  const { palette } = useThemePalette();
  const [mounted, setMounted] = useState(false);
  const [showShootingStar, setShowShootingStar] = useState(false);
  const shootingStarTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  const stars = useMemo(() => generateStars(150), []);
  const themeColors = useMemo(() => getThemeColors(palette), [palette]);
  
  const isLunarMoon = palette === 'lunar-moon';
  const isAndromeda = palette === 'andromeda';

  useEffect(() => {
    setMounted(true);
    return () => {
      if (shootingStarTimeoutRef.current) {
        clearTimeout(shootingStarTimeoutRef.current);
      }
    };
  }, []);

  // Shooting star scheduler
  useEffect(() => {
    if (!cinemaMode || prefersReducedMotion) return;
    
    const scheduleShootingStar = () => {
      const delay = (Math.random() * 60 + 30) * 1000; // 30-90 seconds
      shootingStarTimeoutRef.current = setTimeout(() => {
        setShowShootingStar(true);
        setTimeout(() => setShowShootingStar(false), 1500);
        scheduleShootingStar();
      }, delay);
    };
    
    scheduleShootingStar();
    
    return () => {
      if (shootingStarTimeoutRef.current) {
        clearTimeout(shootingStarTimeoutRef.current);
      }
    };
  }, [cinemaMode, prefersReducedMotion]);

  if (!mounted || !cinemaMode) return null;

  return (
    <div className="fixed inset-0 pointer-events-none z-0">
      {/* Pure black space void */}
      <div className="absolute inset-0 bg-black" />

      {/* Ambient glow breathing - themed */}
      {!prefersReducedMotion && (
        <div
          className="absolute inset-0 opacity-40"
          style={{
            background: isLunarMoon
              ? 'radial-gradient(ellipse at 50% 85%, rgba(100, 100, 120, 0.2) 0%, transparent 55%)'
              : `radial-gradient(ellipse at 50% 85%, ${themeColors[0]}33 0%, ${themeColors[1]}1F 30%, transparent 58%)`,
            animation: 'glowBreathing 12s ease-in-out infinite',
          }}
        />
      )}

      {/* Andromeda — procedural starfield spiral.
          The galaxy is rendered as ~2500 individual stars distributed
          along a 2-arm logarithmic spiral with density falling off
          toward the edge. Color varies by radius (gold core → orange
          dust → blue young stars → violet halo). The dust lane is
          NOT drawn — it's a gap in star density across the disc
          plane, the same trick that makes real galaxy photos read as
          galaxies. Soft glow ellipses behind + in front of the stars
          give the core and disc their luminous quality without
          smudging the star points themselves. */}
      {isAndromeda && (
        <AndromedaGalaxyField prefersReducedMotion={prefersReducedMotion} />
      )}

      {/* Starfield layers - WHITE for Lunar Moon, THEMED COLORS for others */}
      <div className="absolute inset-0">
        {stars.map((star) => {
          const driftDuration =
            star.layer === 'far' ? '55s' : star.layer === 'mid' ? '35s' : '20s';

          const shouldAnimate = !prefersReducedMotion;

          // Lunar Moon = white stars, Others = theme colored stars
          const starColor = isLunarMoon
            ? '#ffffff'
            : themeColors[star.colorIndex % themeColors.length];

          return (
            <div
              key={star.id}
              className="absolute rounded-full"
              style={{
                left: `${star.x}%`,
                top: `${star.y}%`,
                width: `${star.size}px`,
                height: `${star.size}px`,
                backgroundColor: starColor,
                opacity: star.opacity,
                mixBlendMode: isLunarMoon ? 'normal' : 'screen',
                boxShadow:
                  star.layer === 'near'
                    ? `0 0 ${star.size * 5}px ${starColor}CC, 0 0 ${star.size * 10}px ${starColor}66, 0 0 ${star.size * 16}px ${starColor}33`
                    : star.layer === 'mid'
                      ? `0 0 ${star.size * 4}px ${starColor}AA, 0 0 ${star.size * 8}px ${starColor}55`
                      : `0 0 ${star.size * 3}px ${starColor}88`,
                animation: prefersReducedMotion
                  ? 'none'
                  : `
                    starTwinkle ${star.twinkleDuration}s ease-in-out ${star.twinkleDelay}s infinite
                    ${shouldAnimate ? `, starDrift ${driftDuration} linear infinite` : ''}
                  `,
              }}
            />
          );
        })}
      </div>

      {/* Distant Earth (only for Lunar Moon) */}
      {isLunarMoon && (
        <div
          className="absolute"
          style={{
            width: '30px',
            height: '30px',
            top: '12%',
            right: '18%',
            borderRadius: '50%',
            background: 'radial-gradient(circle at 30% 30%, #5a9ed9 0%, #2a6aa8 50%, #0d3a6e 100%)',
            boxShadow: '0 0 20px rgba(90, 158, 217, 0.3), inset -6px -6px 12px rgba(0,0,0,0.4)',
            opacity: 0.7,
          }}
        />
      )}

      {/* Shooting star - themed color */}
      {showShootingStar && !prefersReducedMotion && (
        <div
          className="absolute"
          style={{
            top: `${10 + Math.random() * 20}%`,
            left: `${60 + Math.random() * 25}%`,
            width: '3px',
            height: '3px',
            background: isLunarMoon ? 'white' : themeColors[0],
            borderRadius: '50%',
            boxShadow: isLunarMoon 
              ? '0 0 8px 3px rgba(255, 255, 255, 0.9), -50px 0 25px 2px rgba(255, 255, 255, 0.5), -100px 0 40px 1px rgba(255, 255, 255, 0.3)'
              : `0 0 8px 3px ${themeColors[0]}DD, -50px 0 25px 2px ${themeColors[0]}88, -100px 0 40px 1px ${themeColors[0]}44`,
            animation: 'shootingStar 1.5s ease-out forwards',
          }}
        />
      )}

      {/* Lunar Moon — textured moon image anchored to the bottom of
          the viewport. Other themes intentionally render no big
          celestial object: the studio asked for black + theme-colored
          stars as the default, with Andromeda (handled above) as the
          one themed exception. */}
      {isLunarMoon && (
        <div
          className="fixed"
          style={{
            left: '50%',
            bottom: '-18%',
            width: 'clamp(1200px, 160vw, 2800px)',
            transform: 'translateX(-50%)',
            zIndex: 0,
          }}
        >
          <img
            src={lunarMoonCinema}
            alt=""
            className="block w-full h-auto"
            style={{ opacity: 1, filter: 'none' }}
          />
        </div>
      )}

      {/* Frosted glass overlay for UI depth */}
      <div 
        className="fixed inset-0 pointer-events-none"
        style={{
          background: 'linear-gradient(180deg, transparent 0%, transparent 60%, rgba(0,0,0,0.3) 100%)',
        }}
      />

      {/* Cinema Mode animations */}
      <style>{`
        @keyframes starTwinkle {
          0%, 100% { opacity: var(--star-opacity, 0.4); transform: scale(1); }
          50% { opacity: calc(var(--star-opacity, 0.4) * 2); transform: scale(1.2); }
        }

        @keyframes starDrift {
          0% { transform: translate(0, 0); }
          25% { transform: translate(-10px, 5px); }
          50% { transform: translate(-20px, 0); }
          75% { transform: translate(-10px, -5px); }
          100% { transform: translate(0, 0); }
        }

        @keyframes glowBreathing {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.5; }
        }

        /* Andromeda backdrop — the spiral elements compose this onto
           the canvas; each layer applies it with its own duration so
           the disc, halo, and core drift at different rates. The
           initial rotate(-22deg) is baked into transform on the
           layers themselves so the galaxy still reads as inclined
           even while spinning. */
        @keyframes galaxyRotate {
          from { transform: rotate(-22deg); }
          to   { transform: rotate(338deg); }
        }

        @keyframes shootingStar {
          0% {
            transform: translate(0, 0) rotate(-35deg);
            opacity: 1;
          }
          100% {
            transform: translate(-350px, 210px) rotate(-35deg);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default CinemaModeBackground;

// ─── Andromeda galaxy field ─────────────────────────────────────────
// Procedurally-generated starfield in the shape of an inclined two-arm
// spiral galaxy. ~2500 stars positioned along a logarithmic spiral
// with density falling off toward the edge; color, size, and opacity
// vary by radius so the core reads as bright gold-white, the arms
// pick up orange dust + occasional pink HII regions, and the outer
// reaches feel cool blue-violet. The dust lane is a deliberate gap
// in star density across the disc plane — the trick that makes
// real galaxy photos read as galaxies.

interface AndromedaStar {
  x: number;
  y: number;
  size: number;
  color: string;
  opacity: number;
}

/**
 * Generate the static star positions for the Andromeda spiral.
 * Called ONCE per browser session at module load — the positions are
 * deliberately stable across renders so the galaxy doesn't dance.
 * The visible motion comes from the rotation transform on the wrapper.
 */
const generateAndromedaStars = (): AndromedaStar[] => {
  const stars: AndromedaStar[] = [];
  const totalStars = 2500;
  const armCount = 2;
  const maxRadius = 470;
  const inclineY = 0.32; // squash Y so the disc reads as inclined
  const dustLaneY = 6; // center of the dark dust band, in viewbox units
  const dustLaneHalfHeight = 6; // half-thickness of the lane
  const dustLaneInnerRadius = 60; // dust lane doesn't cut through bright core
  const dustLaneOuterRadius = 300; // and fades out by the outer arms

  for (let i = 0; i < totalStars; i++) {
    // Power-bias toward small t so density falls off with radius.
    const t = Math.pow(Math.random(), 1.4);
    const armIndex = i % armCount;
    const armBaseAngle = (armIndex / armCount) * Math.PI;

    // Logarithmic-spiral angle: increases with radius. ~1.7 turns
    // across the disc gives a visible swirl without going past a
    // full revolution and looking like concentric rings.
    const spiralAngle = armBaseAngle + t * 3.4 * Math.PI;

    // Scatter so the arms have thickness + look natural.
    const radiusJitter = (Math.random() - 0.5) * 0.18;
    const angleJitter = (Math.random() - 0.5) * 0.75;

    const r = Math.max(0, t + radiusJitter) * maxRadius;
    const theta = spiralAngle + angleJitter;

    const x = r * Math.cos(theta);
    const y = r * Math.sin(theta) * inclineY;

    // Carve out the dust lane: skip stars in a thin band across the
    // disc plane between the core and the outer arms. Real Andromeda's
    // signature dark band emerges from interstellar dust occluding the
    // stars behind it; here we just don't render those stars.
    if (
      r > dustLaneInnerRadius &&
      r < dustLaneOuterRadius &&
      Math.abs(y - dustLaneY) < dustLaneHalfHeight
    ) {
      continue;
    }

    // Color by distance from center.
    const rand = Math.random();
    let color: string;
    if (r < 55) {
      color = rand < 0.5 ? '#FFFCF1' : '#FFE8C8';
    } else if (r < 130) {
      color = rand < 0.3 ? '#FFE3B8' : '#FFD4A8';
    } else if (r < 250) {
      // mid disc — warm dust ring with occasional HII pink
      if (rand < 0.04) color = '#FF6E8E';
      else if (rand < 0.5) color = '#E89C68';
      else color = '#FFD4A8';
    } else if (r < 380) {
      // outer arms — younger blue stars and pale yellows
      if (rand < 0.03) color = '#FF85A0';
      else if (rand < 0.35) color = '#A8C8E8';
      else color = '#E8D8C8';
    } else {
      // halo — dim cool stars
      color = rand < 0.5 ? '#8B7AD9' : '#6E5BC3';
    }

    // Size — brighter / bigger near center, plus a sprinkle of
    // "feature stars" at any radius so the field has hierarchy.
    const sizeRand = Math.random();
    let size: number;
    if (sizeRand < 0.025) size = Math.random() * 2.2 + 2.0; // bright feature
    else if (r < 70) size = Math.random() * 1.4 + 0.9;
    else if (r < 280) size = Math.random() * 1.0 + 0.5;
    else size = Math.random() * 0.65 + 0.3;

    // Opacity falls off in the halo so the edges feel like they
    // dissolve into space rather than ending in a hard ring.
    const opacity = r < 60 ? 0.98 : r < 220 ? 0.85 : r < 380 ? 0.65 : 0.42;

    stars.push({ x, y, size, color, opacity });
  }

  return stars;
};

/// Generated once at module load (the positions are stable for the
/// lifetime of the session — no reason to re-randomize on every
/// render of the cinema-mode container).
const ANDROMEDA_STARS = generateAndromedaStars();

interface AndromedaGalaxyFieldProps {
  prefersReducedMotion: boolean;
}

const AndromedaGalaxyField = ({ prefersReducedMotion }: AndromedaGalaxyFieldProps) => (
  <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
    <div
      className="absolute"
      style={{
        width: '180vmax',
        height: '180vmax',
        animation: prefersReducedMotion ? 'none' : 'galaxyRotate 200s linear infinite',
        willChange: prefersReducedMotion ? undefined : 'transform',
        transform: 'rotate(-22deg)',
      }}
    >
      <svg
        viewBox="-500 -500 1000 1000"
        xmlns="http://www.w3.org/2000/svg"
        style={{ width: '100%', height: '100%', display: 'block' }}
        aria-hidden="true"
      >
        <defs>
          {/* Disc bloom — soft warm glow that sits BEHIND the stars
              and lights up the spiral region without softening the
              star points themselves. */}
          <radialGradient id="andromeda-disc-bloom" cx="0" cy="0" r="0.5">
            <stop offset="0%" stopColor="#D17A4F" stopOpacity="0.18" />
            <stop offset="35%" stopColor="#B85A2E" stopOpacity="0.12" />
            <stop offset="70%" stopColor="#5C3D8A" stopOpacity="0.06" />
            <stop offset="100%" stopColor="#1A1535" stopOpacity="0" />
          </radialGradient>

          {/* Core bloom — bright cream-gold haze on top of the stars,
              the dense-star-overlap look you get in any galaxy photo
              where individual stars blend into one bright bulge. */}
          <radialGradient id="andromeda-core-bloom" cx="0" cy="0" r="0.5">
            <stop offset="0%" stopColor="#FFFCF1" stopOpacity="0.55" />
            <stop offset="22%" stopColor="#FFE3B8" stopOpacity="0.30" />
            <stop offset="55%" stopColor="#E8A55A" stopOpacity="0.12" />
            <stop offset="100%" stopColor="#000" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Disc bloom BEHIND the stars. Two ellipses (one wide, one
            tighter to the disc) so the spiral has warmth + breadth. */}
        <ellipse cx="0" cy="0" rx="440" ry="135" fill="url(#andromeda-disc-bloom)" />
        <ellipse cx="0" cy="0" rx="340" ry="80" fill="url(#andromeda-disc-bloom)" opacity="0.7" />

        {/* The actual galaxy: ~2500 procedurally placed stars. */}
        <g style={{ mixBlendMode: 'screen' }}>
          {ANDROMEDA_STARS.map((s, i) => (
            <circle
              key={i}
              cx={s.x}
              cy={s.y}
              r={s.size}
              fill={s.color}
              opacity={s.opacity}
            />
          ))}
        </g>

        {/* Core bloom IN FRONT of the stars — the bright bulge that
            sits over the densest part of the field. Renders last so
            it overlays as a soft haze on top of all those center stars. */}
        <ellipse cx="0" cy="0" rx="110" ry="34" fill="url(#andromeda-core-bloom)" />
        <ellipse cx="0" cy="0" rx="40" ry="13" fill="#FFFCF1" opacity="0.55" />
      </svg>
    </div>
  </div>
);
